import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  PaymentButtonSlot,
  PaymentFooter,
  type PaymentPolicyHelpPanelContext,
} from "components/PaymentButton";
import Skeleton from "components/Skeleton";
import { SrOnly } from "components/SrOnly";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import type { StripePaymentIntentResponseDto } from "types/api/stripe";
import { toastResponseError } from "utils/responseMessageHelper";
import {
  extractStripePaymentIntent,
  StripePaymentPendingError,
  StripePaymentTerminalError,
} from "utils/stripe";
import {
  StripeAmount,
  StripeEditButton,
  StripeElementWrapper,
  StripeFormWrapper,
} from "./StripePaymentForm.styles";

const stripePromises = new Map<string, Promise<Stripe | null>>();

const stripeForKey = (publishableKey: string) => {
  const existing = stripePromises.get(publishableKey);
  if (existing) return existing;

  const promise = loadStripe(publishableKey);
  stripePromises.set(publishableKey, promise);
  return promise;
};

type StripeCheckoutProps = {
  paymentIntent: StripePaymentIntentResponseDto;
  returnUrl: string;
  completePaymentIntent: (paymentIntentId: string) => Promise<void>;
  helpPanelContext?: PaymentPolicyHelpPanelContext;
  onEdit?: () => void;
};

type StripeCheckoutStage = "collecting" | "confirming" | "pending" | "complete";

const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  paymentIntent,
  returnUrl,
  completePaymentIntent,
  helpPanelContext,
  onEdit,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t, i18n } = useTranslation("common");
  const [isReady, setIsReady] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [stage, setStage] = useState<StripeCheckoutStage>("collecting");
  const isProcessingRef = useRef(false);
  const formattedAmount = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: paymentIntent.currency.toUpperCase(),
      }).format(paymentIntent.amount),
    [i18n.language, paymentIntent.amount, paymentIntent.currency],
  );

  const handlePayment = useCallback(async () => {
    if (!stripe || !elements || isProcessingRef.current) return;

    isProcessingRef.current = true;
    let stripeConfirmed = false;

    try {
      setStage("confirming");
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });

      if (result.error) throw result.error;
      if (!result.paymentIntent) {
        throw new Error("Stripe confirmation returned no PaymentIntent");
      }

      stripeConfirmed = true;
      await completePaymentIntent(result.paymentIntent.id);
      setStage("complete");
    } catch (error) {
      if (!stripeConfirmed) {
        setStage("collecting");
        toastResponseError(error);
      } else if (error instanceof StripePaymentTerminalError) {
        setStage("collecting");
        toastResponseError(t("common.stripePaymentFailed"));
      } else {
        setStage("pending");
        if (error instanceof StripePaymentPendingError) {
          toast.info(t("common.stripeProcessing"), { icon: false });
        } else {
          toastResponseError(error);
        }
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [completePaymentIntent, elements, returnUrl, stripe, t]);

  return (
    <PaymentButtonSlot>
      <StripeFormWrapper>
        <StripeAmount>
          {t("common.stripePay", { amount: formattedAmount })}
        </StripeAmount>
        <StripeElementWrapper>
          {!isReady && (
            <div aria-live="polite" role="status">
              <Skeleton height="120px" borderRadius="4px" />
              <SrOnly>{t("common.stripeLoading")}</SrOnly>
            </div>
          )}
          <div style={{ display: isReady ? "block" : "none" }}>
            <PaymentElement
              options={{ layout: "tabs" }}
              onReady={() => setIsReady(true)}
              onChange={(event) => setIsComplete(event.complete)}
            />
          </div>
        </StripeElementWrapper>
        {onEdit && (
          <StripeEditButton
            type="button"
            onClick={onEdit}
            disabled={stage === "confirming" || stage === "pending"}
          >
            {t("common.stripeEditAmount")}
          </StripeEditButton>
        )}
        <PaymentFooter
          onClick={handlePayment}
          disabled={
            !stripe ||
            !elements ||
            !isReady ||
            !isComplete ||
            stage !== "collecting"
          }
          isLoading={stage === "confirming"}
          helpPanelContext={helpPanelContext}
        >
          {stage === "pending"
            ? t("common.stripeProcessing")
            : t("common.stripePay", { amount: formattedAmount })}
        </PaymentFooter>
      </StripeFormWrapper>
    </PaymentButtonSlot>
  );
};

type StripePaymentFormProps = {
  createPaymentIntent: () => Promise<unknown | null>;
  completePaymentIntent: (paymentIntentId: string) => Promise<void>;
  returnUrl: string;
  fallbackChildren: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  helpPanelContext?: PaymentPolicyHelpPanelContext;
  onEdit?: () => void;
  onIntentCreated?: (intent: StripePaymentIntentResponseDto) => void;
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = React.memo(
  ({
    createPaymentIntent,
    completePaymentIntent,
    returnUrl,
    fallbackChildren,
    disabled = false,
    isLoading = false,
    helpPanelContext,
    onEdit,
    onIntentCreated,
  }) => {
    const { t } = useTranslation("common");
    const [paymentIntent, setPaymentIntent] =
      useState<StripePaymentIntentResponseDto | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
    const isCreatingRef = useRef(false);

    useEffect(() => {
      if (!paymentIntent) {
        setStripeInstance(null);
        return undefined;
      }

      let isMounted = true;
      const publishableKey = paymentIntent.publishable_key;

      stripeForKey(publishableKey)
        .then((stripe) => {
          if (!stripe) throw new Error("Stripe.js failed to load");
          if (isMounted) {
            setStripeInstance(stripe);
          }
        })
        .catch((error) => {
          stripePromises.delete(publishableKey);
          if (!isMounted) return;

          setPaymentIntent(null);
          setStripeInstance(null);
          onEdit?.();
          toastResponseError(error);
        });

      return () => {
        isMounted = false;
      };
    }, [onEdit, paymentIntent]);

    const handleCreate = useCallback(async () => {
      if (disabled || isLoading || isCreatingRef.current) return;
      isCreatingRef.current = true;
      setIsCreating(true);

      try {
        const response = await createPaymentIntent();
        if (response === null) return;

        const intent = extractStripePaymentIntent(response);
        setPaymentIntent(intent);
        onIntentCreated?.(intent);
      } catch (error) {
        toastResponseError(error);
      } finally {
        isCreatingRef.current = false;
        setIsCreating(false);
      }
    }, [createPaymentIntent, disabled, isLoading, onIntentCreated]);

    const handleEdit = useCallback(() => {
      setPaymentIntent(null);
      setStripeInstance(null);
      onEdit?.();
    }, [onEdit]);

    if (!paymentIntent) {
      return (
        <PaymentButtonSlot>
          <PaymentFooter
            onClick={handleCreate}
            disabled={disabled || isLoading}
            isLoading={isCreating || isLoading}
            helpPanelContext={helpPanelContext}
          >
            {fallbackChildren}
          </PaymentFooter>
        </PaymentButtonSlot>
      );
    }

    if (!stripeInstance) {
      return (
        <PaymentButtonSlot aria-live="polite" role="status">
          <Skeleton height="48px" borderRadius="4px" />
          <SrOnly>{t("common.stripeLoading")}</SrOnly>
        </PaymentButtonSlot>
      );
    }

    return (
      <Elements
        stripe={stripeInstance}
        options={{
          clientSecret: paymentIntent.client_secret,
          loader: "auto",
        }}
      >
        <StripeCheckout
          paymentIntent={paymentIntent}
          returnUrl={returnUrl}
          completePaymentIntent={completePaymentIntent}
          helpPanelContext={helpPanelContext}
          onEdit={onEdit ? handleEdit : undefined}
        />
      </Elements>
    );
  },
);

export default StripePaymentForm;
